import { memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { TabsSelectorItem } from './TabsSelectorItem'
import { TabsSelectorOption } from './types'
import { TabsSelectorMenu } from './TabsSelectorMenu'

interface Props<V extends string> {
  options: TabsSelectorOption<V>[]
  selectedValue?: V
  visibleItems?: number
  menuLabel?: string
  className?: string
  onSelect: (value: V) => void
}

const MENU_ITEM_VALUE = 'TabsSelectorOption::MENU_ITEM'

const TabsSelectorComponent = <V extends string>({
  options,
  selectedValue,
  className,
  visibleItems = 4,
  menuLabel = 'Select',
  onSelect,
}: Props<V>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  const handleOpenMenu = useCallback((event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSelect = useCallback(
    (event: MouseEvent<HTMLDivElement>, value: V) => {
      onSelect(value)
    },
    [onSelect],
  )

  const isMenuVisible = options.length > visibleItems

  const mainOptions = useMemo(() => {
    return isMenuVisible ? options.slice(0, visibleItems - 1) : options
  }, [options, visibleItems, isMenuVisible])

  const menuOptions = useMemo(() => {
    return isMenuVisible ? options.slice(visibleItems - 1) : []
  }, [options, visibleItems, isMenuVisible])

  const selectedMenuItem = menuOptions.find((item) => item.value === selectedValue)

  return (
    <div
      css={[tw`border-0 border-b border-solid border-[#DAD9E0] flex flex-row flex-1`, className]}
      className={className}
    >
      {mainOptions.map((option) => (
        <TabsSelectorItem
          key={option.value}
          label={option.label}
          value={option.value}
          count={option.count}
          showDot={option.showDot}
          selected={selectedValue === option.value}
          onSelect={handleSelect}
        />
      ))}

      {isMenuVisible && (
        <TabsSelectorItem
          label={selectedMenuItem?.label || menuLabel}
          count={selectedMenuItem?.count}
          value={MENU_ITEM_VALUE}
          showArrow
          selected={!!selectedMenuItem}
          onSelect={handleOpenMenu}
        />
      )}

      <TabsSelectorMenu
        anchorEl={anchorEl}
        isOpen={isMenuOpen}
        options={menuOptions}
        menuLabel={menuLabel}
        selectedValue={selectedValue}
        onSelect={onSelect}
        onClose={handleCloseMenu}
      />
    </div>
  )
}

export const TabsSelector = memo(TabsSelectorComponent) as typeof TabsSelectorComponent
